/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

import { Direction } from "@angular/cdk/bidi";
import { EventEmitter, TemplateRef, Type, ViewContainerRef } from "@angular/core";

import { NzButtonShape, NzButtonSize, NzButtonType } from "ng-zorro-antd/button";
import { NzSafeAny } from "ng-zorro-antd/core/types";

export type OnClickCallback<T> = (instance: T) => (false | void | object) | Promise<false | void | object>;

export type ModalModels = "default" | "confirm"; // Different modal styles we have supported

export type ConfirmType = "confirm" | "info" | "success" | "error" | "warning"; // Subtypes of Confirm Modal

export interface StyleObjectLike {
  [key: string]: string;
}

const noopFun = () => void 0;

export class UiModalOptions<T = NzSafeAny, R = NzSafeAny> {
  modalCentered?: boolean = false;
  modalClosable?: boolean = true;
  modalOkLoading?: boolean = false;
  modalOkDisabled?: boolean = false;
  modalCancelDisabled?: boolean = false;
  modalCancelLoading?: boolean = false;
  modalNoAnimation?: boolean = false;
  modalAutofocus?: "ok" | "cancel" | "auto" | null = "auto";
  modalMask?: boolean;
  modalMaskClosable?: boolean;
  modalKeyboard?: boolean = true;
  modalZIndex?: number = 1000;
  modalWidth?: number | string = 520;
  modalCloseIcon?: string | TemplateRef<void> = "close";
  modalOkType?: NzButtonType = "primary";
  modalOkDanger?: boolean = false;
  modalType?: ModalModels = "default";
  modalOnCancel?: EventEmitter<T> | OnClickCallback<T> = noopFun;
  modalOnOk?: EventEmitter<T> | OnClickCallback<T> = noopFun;
  modalComponentParams?: Partial<T>;
  modalMaskStyle?: StyleObjectLike;
  modalBodyStyle?: StyleObjectLike;
  modalWrapClassName?: string;
  modalClassName?: string;
  modalStyle?: object;
  title?: string | TemplateRef<object>;
  footer?: string | TemplateRef<object> | Array<ModalButtonOptions<T>> | null; // Default Modal ONLY
  modalCancelText?: string | null;
  modalOkText?: string | null;
  modalContent?: string | TemplateRef<NzSafeAny> | Type<T>;
  modalCloseOnNavigation?: boolean;
  modalViewContainerRef?: ViewContainerRef;
  // Template use only
  modalAfterOpen?: EventEmitter<void>;
  modalAfterClose?: EventEmitter<R>;

  // Confirm
  modalIconType?: string = "question-circle";
  modalDirection?: Direction;
}

export interface ModalButtonOptions<T = NzSafeAny> {
  label: string;
  type?: NzButtonType;
  danger?: boolean;
  shape?: NzButtonShape;
  ghost?: boolean;
  size?: NzButtonSize;
  autoLoading?: boolean; // Default: true, indicate whether show loading automatically while onClick returned a Promise

  // [NOTE] "componentInstance" will refer to the component's instance when using Component
  show?: boolean | ((this: ModalButtonOptions<T>, contentComponentInstance?: T) => boolean);
  loading?: boolean | ((this: ModalButtonOptions<T>, contentComponentInstance?: T) => boolean); // This prop CAN'T use with autoLoading=true
  disabled?: boolean | ((this: ModalButtonOptions<T>, contentComponentInstance?: T) => boolean);
  onClick?(this: ModalButtonOptions<T>, contentComponentInstance?: T): NzSafeAny | Promise<NzSafeAny>;
  [key: string]: NzSafeAny;
}
